/**
 * Pure SVG arc-path geometry for the CoverageMap component.
 *
 * Phase 9.1 (2026-04-29): single-renderer abstract mode. All arc math lives
 * here so the component stays a thin shell over a string-builder. Re-used by
 * the geographic renderer in Phase 9.4 (after Mercator projection).
 *
 * Conventions:
 *   - SVG y-axis points DOWN. Compass bearings use 0 deg = North = "up" =
 *     negative-y in SVG. Therefore the conversion to SVG-radians is:
 *
 *         svgRad = ((90 - bearingDeg) * Math.PI) / 180
 *
 *     which sends 0 deg (N) -> +Math.PI / 2 in raw math but reads "up" in
 *     SVG once we use it as `cx + r * cos(svgRad)`, `cy - r * sin(svgRad)`.
 *     We use the cos / -sin pair below so a North bearing renders pointing
 *     toward the top of the canvas.
 *   - Compass bearings sweep CLOCKWISE (positive deg = right of North).
 *   - Beamwidth is the FULL angular width of the wedge in degrees; the wedge
 *     is centered on the bearing, so start = bearing - beamwidth / 2 and
 *     end = bearing + beamwidth / 2.
 *
 * Edge cases (all unit-tested):
 *   - 0 deg span -> returns empty string. Caller MUST check for empty and
 *     skip the <path> entirely. Returning "" rather than "M..." prevents
 *     SVG path parsers from drawing a degenerate visual.
 *   - >= 360 deg span -> returns full-annulus path (two concentric circles
 *     with even-odd fill rule via the M/A/Z, M/A/Z structure).
 *   - > 180 deg span -> uses largeArcFlag = 1 so the SVG renderer takes the
 *     long way around.
 */

/**
 * Convert a compass bearing (0 deg = North, clockwise positive) to a unit
 * vector in SVG coordinates.
 *
 * SVG y-axis points DOWN, so "North" (up on screen) is -y. We compute the
 * standard math angle from the +x axis: math = 90 - compass. Then:
 *   - x component =  cos(math)
 *   - y component = -sin(math)   (negation because SVG y is flipped)
 *
 * @param bearingDeg Compass bearing in degrees (0 = N, 90 = E, 180 = S, 270 = W).
 * @returns Unit vector { x, y } in SVG coordinates.
 */
export function bearingToSvgUnitVector(bearingDeg: number): {
  x: number;
  y: number;
} {
  const mathRad = ((90 - bearingDeg) * Math.PI) / 180;
  return { x: Math.cos(mathRad), y: -Math.sin(mathRad) };
}

/**
 * Compute a point on a circle at center (cx, cy) with radius r at the given
 * compass bearing.
 *
 * @param cx Center x.
 * @param cy Center y.
 * @param r Radius.
 * @param bearingDeg Compass bearing in degrees.
 * @returns Point { x, y } in SVG coordinates.
 */
export function pointOnCircle(
  cx: number,
  cy: number,
  r: number,
  bearingDeg: number,
): { x: number; y: number } {
  const v = bearingToSvgUnitVector(bearingDeg);
  return { x: cx + r * v.x, y: cy + r * v.y };
}

/**
 * Normalize an angle span to the range [0, 360]. Negative spans collapse to
 * 0 (no wedge); spans > 360 clamp to 360 (full annulus).
 *
 * @param spanDeg Raw angle span in degrees.
 * @returns Clamped span in [0, 360].
 */
function clampSpan(spanDeg: number): number {
  if (!Number.isFinite(spanDeg) || spanDeg <= 0) return 0;
  if (spanDeg >= 360) return 360;
  return spanDeg;
}

/**
 * Build a full-annulus SVG path (a ring with a hole). Uses two concentric
 * circles drawn in opposite winding so the even-odd fill rule (default in
 * SVG) carves out the inner disc.
 *
 * Each circle is drawn as two 180 deg arcs because SVG's elliptical-arc
 * command rejects a single arc from a point to itself.
 *
 * @param cx Center x.
 * @param cy Center y.
 * @param innerR Inner radius (must be >= 0).
 * @param outerR Outer radius (must be > innerR).
 * @returns SVG `d` string for a ring.
 */
function fullAnnulusPath(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
): string {
  // Outer circle (clockwise) + inner circle (counter-clockwise via negative
  // sweep flag). Two-arc form because SVG rejects 0-length arcs.
  const ox1 = cx + outerR;
  const ox2 = cx - outerR;
  const ix1 = cx + innerR;
  const ix2 = cx - innerR;
  return [
    `M ${ox1} ${cy}`,
    `A ${outerR} ${outerR} 0 1 1 ${ox2} ${cy}`,
    `A ${outerR} ${outerR} 0 1 1 ${ox1} ${cy}`,
    `Z`,
    `M ${ix1} ${cy}`,
    `A ${innerR} ${innerR} 0 1 0 ${ix2} ${cy}`,
    `A ${innerR} ${innerR} 0 1 0 ${ix1} ${cy}`,
    `Z`,
  ].join(' ');
}

/**
 * Build an SVG `d` string for an annular arc (a "wedge with a hole" or
 * "donut slice"). The wedge is bounded by:
 *   - inner arc on radius innerR from startDeg to endDeg
 *   - radial line at endDeg from innerR -> outerR
 *   - outer arc on radius outerR from endDeg back to startDeg
 *   - radial line at startDeg from outerR -> innerR
 *
 * Compass bearings are converted internally to SVG radians.
 *
 * @param cx Center x in SVG user units.
 * @param cy Center y in SVG user units.
 * @param innerR Inner radius (>= 0).
 * @param outerR Outer radius (> innerR).
 * @param startDeg Start compass bearing (deg, 0 = N, CW positive).
 * @param endDeg End compass bearing (deg, 0 = N, CW positive).
 * @returns SVG `d` string. Empty string when the angular span is zero.
 *
 * Edge cases:
 *   - span <= 0 -> "" (caller should skip rendering).
 *   - span >= 360 -> full annulus (ring with hole, even-odd fill).
 *   - span > 180 -> largeArcFlag = 1.
 *   - innerR === 0 -> classic pie slice (wedge from center).
 */
export function annularArcPath(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startDeg: number,
  endDeg: number,
): string {
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return '';
  if (!Number.isFinite(innerR) || !Number.isFinite(outerR)) return '';
  if (innerR < 0 || outerR <= innerR) return '';

  const rawSpan = endDeg - startDeg;
  const span = clampSpan(rawSpan);

  if (span === 0) return '';
  if (span >= 360) return fullAnnulusPath(cx, cy, innerR, outerR);

  // Compass arcs sweep clockwise. In SVG coordinates (y-down), clockwise on
  // the visual screen also corresponds to sweepFlag = 1 because the
  // bearingToSvgUnitVector helper inverts y. We pass sweepFlag = 1 for the
  // outer arc and sweepFlag = 0 for the return inner arc so the path
  // describes a closed wedge that does not self-intersect.
  const largeArcFlag = span > 180 ? 1 : 0;

  const pStartOuter = pointOnCircle(cx, cy, outerR, startDeg);
  const pEndOuter = pointOnCircle(cx, cy, outerR, endDeg);
  const pStartInner = pointOnCircle(cx, cy, innerR, startDeg);
  const pEndInner = pointOnCircle(cx, cy, innerR, endDeg);

  // For zero-inner-radius (pie slice): skip the inner arc entirely; collapse
  // to a single LineTo back to the center.
  if (innerR === 0) {
    return [
      `M ${cx} ${cy}`,
      `L ${pStartOuter.x} ${pStartOuter.y}`,
      `A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${pEndOuter.x} ${pEndOuter.y}`,
      `Z`,
    ].join(' ');
  }

  return [
    `M ${pStartOuter.x} ${pStartOuter.y}`,
    `A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${pEndOuter.x} ${pEndOuter.y}`,
    `L ${pEndInner.x} ${pEndInner.y}`,
    `A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${pStartInner.x} ${pStartInner.y}`,
    `Z`,
  ].join(' ');
}

/**
 * Convenience: compute the wedge path for a directional zone given the
 * antenna bearing + beamwidth. Centers the wedge on the bearing.
 *
 * @param cx Center x.
 * @param cy Center y.
 * @param innerR Inner radius.
 * @param outerR Outer radius.
 * @param bearingDeg Compass bearing of the antenna boresight.
 * @param beamwidthDeg Total beamwidth (full angular width).
 * @returns SVG `d` string. Empty when beamwidth <= 0.
 */
export function directionalWedgePath(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  bearingDeg: number,
  beamwidthDeg: number,
): string {
  if (!Number.isFinite(beamwidthDeg) || beamwidthDeg <= 0) return '';
  const half = beamwidthDeg / 2;
  return annularArcPath(
    cx,
    cy,
    innerR,
    outerR,
    bearingDeg - half,
    bearingDeg + half,
  );
}

/**
 * Tier ring radii.
 *
 * Inner radius = 30 + (sequenceOrder - 1) * 40
 * Outer radius = inner + 40
 *
 * sequenceOrder is 1-based per the upstream zones table.
 *
 * @param sequenceOrder 1-based tier order.
 * @returns { innerR, outerR } in SVG user units (viewBox 0 0 400 400).
 */
export function tierRadii(sequenceOrder: number): {
  innerR: number;
  outerR: number;
} {
  const safeOrder = Math.max(1, Math.floor(sequenceOrder));
  const innerR = 30 + (safeOrder - 1) * 40;
  const outerR = innerR + 40;
  return { innerR, outerR };
}
